import { expandValue } from 'lib/rpcts'
import { GetAutomationResponse, UpdateAutomationResponse, Automation, AutomationTemplateRef } from 'pbts/c1/api/automations/v1/automations'
import { AutomationSearchServiceClient, AutomationServiceClient } from 'pbts/c1/api/automations/v1/automations.client'
import { GetAutomationExecutionStepOutputResponse, ListAutomationExecutionStepsResponse, SearchAutomationExecutionsResponse, AutomationExecution, AutomationExecutionRef, AutomationExecutionStep, AutomationExecutionTrigger, AutomationExecutionView, AutomationExecutionStepRef, SearchAutomationExecutionStepsResponse, GetAutomationExecutionResponse } from 'pbts/c1/api/automations/v1/automations_executions'
import { AutomationExecutionActionsServiceClient, AutomationExecutionSearchServiceClient, AutomationExecutionServiceClient } from 'pbts/c1/api/automations/v1/automations_executions.client'
import { Any } from 'pbts/google/protobuf/any'
import { createMutationApi } from './lib/createMutationApi'
import { createPagedListApi } from './lib/createPagedListApi'
import { createUnaryApi } from './lib/createUnaryApi'

export type WorkflowExportedContextResponse =
    | { success: object; error?: never }
    | { success?: never; error: string }

export const searchAutomationsApi = createPagedListApi(
    AutomationSearchServiceClient,
    'searchAutomations',
    (response) => response.list,
    AutomationTemplateRef.typeName,
)

export interface WorkflowExecutionValue extends AutomationExecution {
    automation?: Automation
    automationExecutionTrigger?: AutomationExecutionTrigger
}

const expandWorkflowExecutionView = (msg: AutomationExecutionView | undefined, values: Any[]): WorkflowExecutionValue => ({
    ...AutomationExecution.create(msg?.automationExecution ?? {}),
    automation: expandValue<Automation>(msg?.automationPath, Automation, values),
    automationExecutionTrigger: expandValue<AutomationExecutionTrigger>(
        msg?.automationExecutionTriggerPath,
        AutomationExecutionTrigger,
        values,
    ),
})

const transformWorkflowExecutionViews = (resp: SearchAutomationExecutionsResponse): Array<WorkflowExecutionValue> => {
    return resp.list.map(
        (v): WorkflowExecutionValue => expandWorkflowExecutionView(v, resp.expanded),
    )
}

const transformWorkflowExecutionStepOutput = (res: GetAutomationExecutionStepOutputResponse): WorkflowExportedContextResponse => {
    const { output } = res
    if (!output) {
        return { success: {} }
    }

    switch (output.kind.oneofKind) {
        case 'success': {
            const contextValue = output.kind.success.exportedContext
                ?.context?.fields?.context?.kind?.oneofKind === 'stringValue'
                ? output.kind.success.exportedContext.context.fields.context.kind.stringValue
                : null

            if (!contextValue) {
                return { success: {} }
            }

            try {
                return { success: JSON.parse(contextValue) }
            } catch {
                return { error: 'Invalid JSON in context value' }
            }
        }
        case 'error':
            return { error: output.kind.error.error }
        default:
            return { success: {} }
    }
}

export const searchWorkflowExecutionsApi = createPagedListApi(
    AutomationExecutionSearchServiceClient,
    'searchAutomationExecutions',
    transformWorkflowExecutionViews,
    AutomationExecutionRef.typeName,
)

export const getWorkflowApi = createUnaryApi(
    AutomationServiceClient,
    'getAutomation',
    (res: GetAutomationResponse): Automation => Automation.create(res.automation ?? {}),
    AutomationTemplateRef.typeName,
)

export const getWorkflowExecutionApi = createUnaryApi(
    AutomationExecutionServiceClient,
    'getAutomationExecution',
    (res: GetAutomationExecutionResponse): WorkflowExecutionValue => expandWorkflowExecutionView(res.view, res.expanded),
)

export const listWorkflowExecutionStepApi = createUnaryApi(
    AutomationExecutionServiceClient,
    'listAutomationExecutionSteps',
    (res: ListAutomationExecutionStepsResponse): AutomationExecutionStep[] => {
        return res.automationExecutionSteps.map((step) => AutomationExecutionStep.create(step))
    },
    AutomationExecutionRef.typeName,
)

export const searchWorkflowExecutionStepApi = createPagedListApi(
    AutomationExecutionSearchServiceClient,
    'searchAutomationExecutionSteps',
    (res: SearchAutomationExecutionStepsResponse): AutomationExecutionStep[] => {
        return res.list.map((step) => AutomationExecutionStep.create(step))
    },
    AutomationExecutionStepRef.typeName,
)

export const getWorkflowExecutionStepOutputApi = createUnaryApi(
    AutomationExecutionServiceClient,
    'getAutomationExecutionStepOutput',
    transformWorkflowExecutionStepOutput,
    AutomationExecutionRef.typeName,
)

export const createWorkflowApi = createMutationApi(
    AutomationServiceClient,
    'createAutomation',
)

export const updateWorkflowApi = createMutationApi(
    AutomationServiceClient,
    'updateAutomation',
    (res: UpdateAutomationResponse) => ({
        automation: Automation.create(res.automation ?? {}),
        webhookHmacSecret: res.webhookHmacSecret ?? '',
    }),
    AutomationTemplateRef.typeName,
)

export const deleteWorkflowApi = createMutationApi(
    AutomationServiceClient,
    'deleteAutomation',
    undefined,
)

export const executeWorkflowApi = createMutationApi(
    AutomationServiceClient,
    'executeAutomation',
)

export const terminateWorkflowApi = createMutationApi(
    AutomationExecutionActionsServiceClient,
    'terminateAutomation',
)

export const searchWorkflowTemplateVersionsApi = createPagedListApi(
    AutomationSearchServiceClient,
    'searchAutomationTemplateVersions',
    (response) => response.list,
    AutomationTemplateRef.typeName,
)

export const searchWorkflowExecutionCountForAppApi = createUnaryApi(
    AutomationExecutionSearchServiceClient,
    'searchAutomationExecutionCountForApp',
)
